import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "activeWorkout_v1";

const ActiveWorkoutContext = createContext({
  active: null,
  elapsedSeconds: 0,
  startWorkout: () => {},
  resumeWorkout: () => {},
  saveAndExit: () => {},
  endWorkout: () => {},
  togglePauseTimer: () => {},
  updateActiveState: () => {},
  completeCurrentSet: () => {},
  skipRest: () => {},
  addRestSeconds: () => {},
  goToNextExercise: () => {},
  goToPreviousExercise: () => {},
  summary: null,
  setSummary: () => {},
  clearSummary: () => {},
});

export function normalizeExercises(exercises) {
  if (!exercises) return [];
  let list = exercises;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch (_e) {
      list = [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === "string") {
        try {
          const parsed = JSON.parse(item);
          if (parsed && typeof parsed === "object") return parsed;
        } catch {}
        return { name: item };
      }
      return item;
    })
    .filter(Boolean);
}

/**
 * Manages the currently-running workout across the whole app. State is
 * mirrored to localStorage so a refresh (or backgrounding the app) doesn't
 * lose progress.
 */
export function ActiveWorkoutProvider({ children }) {
  const [active, setActive] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;

      // Auto-clear stale workouts (> 8 hours old or invalid timestamp)
      if (parsed.started_at) {
        const startedTime = new Date(parsed.started_at).getTime();
        if (Number.isNaN(startedTime) || Date.now() - startedTime > 8 * 60 * 60 * 1000) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      }

      // Auto-clear sessions that have no plan and no exercises
      const hasPlan = Boolean(parsed.plan_id || parsed.id);
      const hasExercises = Array.isArray(parsed.exercises) && parsed.exercises.length > 0;
      if (!hasPlan && !hasExercises) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch (_e) {
      return null;
    }
  });

  const [elapsedSeconds, setElapsedSeconds] = useState(() => computeElapsed(active));
  const [summary, setSummary] = useState(null);

  // Tick once per second for workout timer and rest timer
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setElapsedSeconds(computeElapsed(active));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  const elapsedForDisplay = active ? Math.max(elapsedSeconds, computeElapsed(active)) : 0;

  // Persist whenever active changes
  useEffect(() => {
    if (active) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(active));
      } catch (_e) {
        /* ignore quota errors */
      }
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [active]);

  const startWorkout = useCallback((planOrMeta, metaOptions = {}) => {
    const isPlan = planOrMeta && typeof planOrMeta === "object" && (planOrMeta.exercises || planOrMeta.name);
    const plan = isPlan ? planOrMeta : null;
    const planId = plan?.id || plan?._id || planOrMeta?.plan_id || (typeof planOrMeta === "string" ? planOrMeta : "");
    const planName = plan?.name || planOrMeta?.plan_name || metaOptions?.plan_name || "Workout";
    const rawExercises = plan?.exercises || planOrMeta?.exercises || metaOptions?.exercises || [];
    const normalizedExercises = normalizeExercises(rawExercises);
    const totalExercises = normalizedExercises.length || planOrMeta?.total_exercises || metaOptions?.total_exercises || 0;
    const estMinutes = plan?.estimated_minutes || planOrMeta?.estimated_minutes || metaOptions?.estimated_minutes || 30;

    const nowIso = new Date().toISOString();
    const newSession = {
      plan_id: planId,
      plan_name: planName,
      plan: {
        ...(plan || {}),
        id: planId,
        name: planName,
        exercises: normalizedExercises,
      },
      exercises: normalizedExercises,
      total_exercises: totalExercises,
      estimated_minutes: estMinutes,
      started_at: nowIso,
      last_resumed_at: nowIso,
      accumulated_seconds: 0,
      is_paused: false,
      paused_at: null,
      is_fullscreen: true,
      current_exercise_idx: 0,
      current_set: 1,
      completed_sets: {},
      rest_timer: null,
    };
    setActive(newSession);
  }, []);

  const resumeWorkout = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      const nowIso = new Date().toISOString();
      return {
        ...prev,
        is_fullscreen: true,
        is_paused: false,
        last_resumed_at: nowIso,
        paused_at: null,
      };
    });
  }, []);

  const saveAndExit = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      const now = Date.now();
      const currentElapsed = computeElapsed(prev);
      return {
        ...prev,
        is_fullscreen: false,
        accumulated_seconds: currentElapsed,
        last_resumed_at: new Date(now).toISOString(),
      };
    });
  }, []);

  const togglePauseTimer = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      const now = Date.now();
      if (prev.is_paused) {
        // Resuming
        return {
          ...prev,
          is_paused: false,
          last_resumed_at: new Date(now).toISOString(),
          paused_at: null,
        };
      } else {
        // Pausing
        const currentElapsed = computeElapsed(prev);
        return {
          ...prev,
          is_paused: true,
          accumulated_seconds: currentElapsed,
          paused_at: new Date(now).toISOString(),
        };
      }
    });
  }, []);

  const updateActiveState = useCallback((patch) => {
    setActive((prev) => {
      if (!prev) return null;
      const updated = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      return updated;
    });
  }, []);

  const completeCurrentSet = useCallback(({ weight, reps, targetSets, restSeconds = 60 }) => {
    setActive((prev) => {
      if (!prev) return null;
      const exIdx = prev.current_exercise_idx || 0;
      const setNum = prev.current_set || 1;
      const existingSets = prev.completed_sets?.[exIdx] || [];

      // Avoid duplicate set completion for the same set
      const alreadyDone = existingSets.some((s) => s.setNumber === setNum);
      const updatedSetsForEx = alreadyDone
        ? existingSets.map((s) => s.setNumber === setNum ? { ...s, weight, reps, completedAt: Date.now() } : s)
        : [...existingSets, { setNumber: setNum, weight, reps, completedAt: Date.now() }];

      const updatedCompletedSets = {
        ...(prev.completed_sets || {}),
        [exIdx]: updatedSetsForEx,
      };

      const exercises = prev.plan?.exercises || [];
      const totalExercises = exercises.length || prev.total_exercises || 1;
      const isLastSetOfExercise = setNum >= targetSets;
      const isLastExercise = exIdx >= totalExercises - 1;

      if (isLastSetOfExercise && isLastExercise) {
        // Workout finished! No rest timer needed
        return {
          ...prev,
          completed_sets: updatedCompletedSets,
          rest_timer: null,
        };
      }

      // Start automatic rest timer
      const nextSetNum = isLastSetOfExercise ? 1 : setNum + 1;
      const nextExIdx = isLastSetOfExercise ? exIdx + 1 : exIdx;
      const durationSec = Math.max(10, Number(restSeconds) || 60);
      const endsAt = Date.now() + durationSec * 1000;

      return {
        ...prev,
        completed_sets: updatedCompletedSets,
        rest_timer: {
          active: true,
          duration: durationSec,
          ends_at: endsAt,
          next_exercise_idx: nextExIdx,
          next_set: nextSetNum,
          transitioning_exercise: isLastSetOfExercise,
        },
      };
    });
  }, []);

  const skipRest = useCallback(() => {
    setActive((prev) => {
      if (!prev || !prev.rest_timer) return prev;
      const timer = prev.rest_timer;
      return {
        ...prev,
        current_exercise_idx: timer.next_exercise_idx,
        current_set: timer.next_set,
        rest_timer: null,
      };
    });
  }, []);

  const addRestSeconds = useCallback((sec = 30) => {
    setActive((prev) => {
      if (!prev || !prev.rest_timer) return prev;
      return {
        ...prev,
        rest_timer: {
          ...prev.rest_timer,
          duration: prev.rest_timer.duration + sec,
          ends_at: prev.rest_timer.ends_at + sec * 1000,
        },
      };
    });
  }, []);

  const goToNextExercise = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      const exercises = prev.plan?.exercises || [];
      const total = exercises.length || prev.total_exercises || 1;
      const nextIdx = Math.min(total - 1, (prev.current_exercise_idx || 0) + 1);
      return {
        ...prev,
        current_exercise_idx: nextIdx,
        current_set: 1,
        rest_timer: null,
      };
    });
  }, []);

  const goToPreviousExercise = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      const prevIdx = Math.max(0, (prev.current_exercise_idx || 0) - 1);
      return {
        ...prev,
        current_exercise_idx: prevIdx,
        current_set: 1,
        rest_timer: null,
      };
    });
  }, []);

  const endWorkout = useCallback(() => {
    setActive(null);
  }, []);

  const clearSummary = useCallback(() => setSummary(null), []);

  return (
    <ActiveWorkoutContext.Provider
      value={{
        active,
        elapsedSeconds: elapsedForDisplay,
        startWorkout,
        resumeWorkout,
        saveAndExit,
        endWorkout,
        togglePauseTimer,
        updateActiveState,
        completeCurrentSet,
        skipRest,
        addRestSeconds,
        goToNextExercise,
        goToPreviousExercise,
        summary,
        setSummary,
        clearSummary,
      }}
    >
      {children}
    </ActiveWorkoutContext.Provider>
  );
}

function computeElapsed(active) {
  if (!active?.started_at) return 0;
  const acc = active.accumulated_seconds || 0;
  if (active.is_paused) return acc;
  const lastResumed = active.last_resumed_at
    ? new Date(active.last_resumed_at).getTime()
    : new Date(active.started_at).getTime();
  if (Number.isNaN(lastResumed)) return acc;
  return Math.max(0, acc + Math.floor((Date.now() - lastResumed) / 1000));
}

export function useActiveWorkout() {
  return useContext(ActiveWorkoutContext);
}

/**
 * Format a duration in seconds as HH:MM:SS (always padded).
 */
export function formatElapsed(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/**
 * Shorter MM:SS variant used in the floating timer pill when the workout is
 * under an hour. Falls back to HH:MM:SS otherwise.
 */
export function formatElapsedShort(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  if (total < 3600) {
    const mm = Math.floor(total / 60);
    const ss = total % 60;
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  return formatElapsed(total);
}

/**
 * Simple kcal/min estimator. ~0.094 kcal · kg⁻¹ · min⁻¹ matches the user's
 * ~7 kcal/min target for a 75kg user (75 × 0.094 ≈ 7).
 */
export function estimateCaloriesBurned(weightKg, elapsedSeconds) {
  const kg = Number(weightKg) || 75; // default fallback
  const minutes = elapsedSeconds / 60;
  return Math.max(0, Math.round(kg * 0.094 * minutes));
}
